import { createApp } from 'vue';
import { createPinia } from 'pinia';
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate';
import { VueQueryPlugin } from '@tanstack/vue-query';
import { queryClient } from '@/service/queryClient';
import { createHead } from '@unhead/vue';
import PrimeVue from 'primevue/config';
import { definePreset } from '@primevue/themes';
import Aura from '@primevue/themes/aura';
import ToastService from 'primevue/toastservice';
import ConfirmationService from 'primevue/confirmationservice';
import Tooltip from 'primevue/tooltip';

import App from './App.vue';
import router from './router';
import { loadConfig } from './service/api';

import '@fontsource-variable/inter';
import '@fontsource-variable/fraunces';
import 'primeicons/primeicons.css';
import 'leaflet/dist/leaflet.css';
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css';
import './assets/styles/main.css';

const AguaraPreset = definePreset(Aura, {
    primitive: {
        borderRadius: {
            none: '0',
            xs: '1px',
            sm: '2px',
            md: '4px',
            lg: '6px',
            xl: '8px'
        }
    },
    semantic: {
        primary: {
            50: '#f0f7f4',
            100: '#d1e8db',
            200: '#a3d1b7',
            300: '#6db38c',
            400: '#3f8d63',
            500: '#2a6b47',
            600: '#1B3A2D',
            700: '#163024',
            800: '#11261c',
            900: '#0c1c14',
            950: '#06100b'
        }
    }
});

// Manejar errores de chunks obsoletos después de deploy
const RELOAD_KEY = '__chunk_reload';
function reloadOnceForChunkError() {
    if (sessionStorage.getItem(RELOAD_KEY)) return;
    sessionStorage.setItem(RELOAD_KEY, '1');
    window.location.reload();
}
window.addEventListener('load', () => sessionStorage.removeItem(RELOAD_KEY));

window.addEventListener('vite:preloadError', (event) => {
    event.preventDefault();
    reloadOnceForChunkError();
});

async function init() {
    // Cargar config de runtime (API URL)
    await loadConfig();

    const app = createApp(App);

    // Pinia
    const pinia = createPinia();
    pinia.use(piniaPluginPersistedstate);
    app.use(pinia);

    // Unhead (meta tags por ruta)
    app.use(createHead());

    // Router
    app.use(router);

    // Manejar errores de importación dinámica
    router.onError((error) => {
        const isChunkError =
            error.message?.includes('Failed to fetch dynamically imported module') ||
            error.message?.includes('Importing a module script failed') ||
            error.message?.includes("Couldn't resolve component") ||
            error.name === 'ChunkLoadError';
        if (isChunkError) {
            reloadOnceForChunkError();
        }
    });

    // PrimeVue
    app.use(PrimeVue, {
        theme: {
            preset: AguaraPreset,
            options: {
                darkModeSelector: '.app-dark'
            }
        },
        locale: {
            accept: 'Aceptar',
            reject: 'Cancelar',
            choose: 'Elegir',
            upload: 'Subir',
            cancel: 'Cancelar',
            dayNames: ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'],
            dayNamesShort: ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'],
            dayNamesMin: ['Do', 'Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa'],
            monthNames: [
                'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
                'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
            ],
            monthNamesShort: [
                'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun',
                'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'
            ],
            today: 'Hoy',
            clear: 'Limpiar',
            weekHeader: 'Sem',
            firstDayOfWeek: 1,
            dateFormat: 'dd/mm/yy',
            weak: 'Débil',
            medium: 'Media',
            strong: 'Fuerte',
            passwordPrompt: 'Ingresá una contraseña',
            emptyMessage: 'No se encontraron resultados',
            emptyFilterMessage: 'No se encontraron resultados'
        }
    });

    app.use(ToastService);
    app.use(ConfirmationService);
    app.directive('tooltip', Tooltip);

    // TanStack Query
    app.use(VueQueryPlugin, { queryClient });

    // Esperar a que el router resuelva la navegación inicial antes de montar
    await router.isReady();
    app.mount('#app');
}

init();
